import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { IDigitalProductBillingType } from '../models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../models/digitalProductVisibility.model';
import { IDigitalProduct } from '../models/digitalProduct.model';

type DigitalProductUpsertPayload = {
    name: string;
    description?: string | null;
    coverUrl?: string | null;
    currency: string;
    billingType: IDigitalProductBillingType;
    recurringInterval: IDigitalProductRecurringInterval;
    recurringIntervalCount?: number | null;
    trialDays?: number | null;
    oneTimeFee?: number | null;
    recurringFee?: number | null;
    visibility: IDigitalProductVisibility;
};

@Injectable()
export class DigitalProductService {
    private http = inject(HttpClient);
    authService = inject(AuthenticationService);
    private readonly billingTypeMap: Record<string, IDigitalProductBillingType> = {
        '1': IDigitalProductBillingType.OneTime,
        'OneTime': IDigitalProductBillingType.OneTime,
        '2': IDigitalProductBillingType.Recurring,
        'Recurring': IDigitalProductBillingType.Recurring
    };
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };

    getDigitalProducts(): Observable<IDigitalProduct[]> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (this.hasValidToken(token)) {
            return this.http.get<IDigitalProduct[]>(`${environment.API_URL}/api/DigitalProduct/mine`, options).pipe(
                map((digitalProducts) => digitalProducts.map((digitalProduct) => this.normalizeDigitalProduct(digitalProduct)))
            );
        }

        return of();
    }

    getDigitalProduct(id: string | undefined): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!id || !this.hasValidToken(token)) {
            return of();
        }

        return this.http.get<IDigitalProduct>(`${environment.API_URL}/api/DigitalProduct/public/${id}`, options).pipe(
            map((digitalProduct) => this.normalizeDigitalProduct(digitalProduct))
        );
    }

    getPublicDigitalProductByUserAndSlug(userId: string | undefined, slug: string | undefined): Observable<IDigitalProduct | null> {
        const normalizedUserId = userId?.trim().replace(/^@/, '');
        const normalizedSlug = slug?.trim();

        if (!normalizedUserId || !normalizedSlug) {
            return of(null);
        }

        const encodedUserId = encodeURIComponent(normalizedUserId);
        const encodedSlug = encodeURIComponent(normalizedSlug);
        const pathEndpoint = `${environment.API_URL}/api/DigitalProduct/public/${encodedUserId}/${encodedSlug}`;

        return this.http.get<IDigitalProduct>(pathEndpoint).pipe(
            map((digitalProduct) => this.normalizeDigitalProduct(digitalProduct)),
            catchError(() => of(null))
        );
    }

    createDigitalProduct(digitalProduct: IDigitalProduct): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token)) {
            return of();
        }

        const payload = this.sanitizeDigitalProduct(digitalProduct);
        return this.http.post<IDigitalProduct>(`${environment.API_URL}/api/DigitalProduct/create`, payload, options).pipe(
            map((savedDigitalProduct) => this.normalizeDigitalProduct(savedDigitalProduct))
        );
    }

    editDigitalProduct(digitalProduct: IDigitalProduct): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token) || !digitalProduct.id) {
            return of();
        }

        const payload = this.sanitizeDigitalProduct(digitalProduct);
        return this.http.put<IDigitalProduct>(`${environment.API_URL}/api/DigitalProduct/${digitalProduct.id}`, payload, options).pipe(
            map((updatedDigitalProduct) => this.normalizeDigitalProduct(updatedDigitalProduct))
        );
    }

    deleteDigitalProduct(id: string): Observable<void> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token)) {
            return of();
        }

        return this.http.delete<void>(`${environment.API_URL}/api/DigitalProduct/${id}`, options);
    }

    changeCover(imageData: string | File | null, digitalProductId: string): Observable<string> {
        const token = this.authService.getUserToken();

        if (!this.hasValidToken(token)) {
            return of();
        }

        const formData = new FormData();
        formData.append('digitalProductId', digitalProductId);
        if (imageData instanceof File) {
            formData.append('imageFile', imageData, imageData.name);
        }

        const options = {
            headers: new HttpHeaders({
                'Authorization': `Bearer ${token}`
            }),
            contentType: 'multipart/form-data',
            body: formData
        };

        return this.http.post<string>(`${environment.API_URL}/api/DigitalProduct/updateCover`, formData, options);
    }

    private getAuthOptions(token: string | null): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-type': 'application/json',
                'Authorization': `Bearer ${token}`
            })
        };
    }

    private hasValidToken(token: string | null): token is string {
        return token !== undefined && token !== null && token !== '';
    }

    private sanitizeDigitalProduct(digitalProduct: IDigitalProduct): DigitalProductUpsertPayload {
        const visibility = this.resolveVisibility(digitalProduct.visibility);

        return {
            name: (digitalProduct.name ?? '').trim(),
            description: digitalProduct.description?.trim() ?? null,
            coverUrl: digitalProduct.coverUrl?.trim() ?? null,
            currency: (digitalProduct.currency ?? 'USD').trim().toUpperCase(),
            billingType: IDigitalProductBillingType.OneTime,
            recurringInterval: IDigitalProductRecurringInterval.None,
            recurringIntervalCount: null,
            trialDays: null,
            oneTimeFee: digitalProduct.oneTimeFee ?? null,
            recurringFee: null,
            visibility
        };
    }

    private normalizeDigitalProduct(digitalProduct: IDigitalProduct): IDigitalProduct {
        const billingType = this.resolveBillingType(digitalProduct.billingType);
        const recurring = billingType === IDigitalProductBillingType.Recurring;

        return {
            ...digitalProduct,
            billingType,
            recurringInterval: recurring
                ? this.resolveRecurringInterval(digitalProduct.recurringInterval)
                : IDigitalProductRecurringInterval.None,
            visibility: this.resolveVisibility(digitalProduct.visibility)
        };
    }

    private resolveBillingType(value: unknown): IDigitalProductBillingType {
        return this.resolveEnumValue(value, this.billingTypeMap) ?? IDigitalProductBillingType.OneTime;
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval {
        return this.resolveEnumValue(value, this.recurringIntervalMap) ?? IDigitalProductRecurringInterval.None;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility {
        return this.resolveEnumValue(value, this.visibilityMap) ?? IDigitalProductVisibility.Draft;
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        return map[String(value).trim()];
    }
}
